import React, { useCallback, useEffect, useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { ToastProvider, useToast } from '../components/Toast';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// CRM → Online Store tab. The store shows chosen services in the Client app
// (and white-label apps) with a price or "Request a quote". Every order or
// enquiry also becomes a CRM lead (source "Mobile App"), so the people who
// handle leads handle store orders here too. Payments are manual (UPI / bank
// from Billing Settings), so staff confirm them here.
// Backend: ca-backend/routes/storeRoutes.js.

const STATUS = {
  enquiry:           { label: 'Quote requested',   color: '#2563eb', bg: '#eff6ff' },
  awaiting_payment:  { label: 'Awaiting payment',  color: '#d97706', bg: '#fffbeb' },
  payment_submitted: { label: 'Payment to verify', color: '#7c3aed', bg: '#f5f3ff' },
  confirmed:         { label: 'Confirmed',         color: '#16a34a', bg: '#f0fdf4' },
  cancelled:         { label: 'Cancelled',         color: '#6b7280', bg: '#f3f4f6' },
};
const inr = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const when = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

// Starting text for the refund policy (the firm edits it before publishing).
const REFUND_TEMPLATE = `Refund & Cancellation Policy

1. Cancellation before work starts: you can cancel an order before we begin work on it. We refund the full amount paid, less any government fees already paid on your behalf.
2. After work has started: professional fees for work already done are not refundable. Any unused part of the fee is refunded at our discretion, in proportion to the work pending.
3. Government fees, stamp duty and third-party charges paid on your behalf are not refundable once paid.
4. Delays or rejections by a government department are outside our control and are not a ground for a refund. We will re-submit or follow up at no extra professional fee where the rejection was due to our error.
5. Duplicate or excess payments are refunded in full.
6. How to ask for a refund: write to us with your order number. Approved refunds are made to the original payment method within 7–10 working days.

For any question about your order, contact us using the details in the app.`;

// Native alert()/confirm() are replaced by the shared toast + confirm card (components/Toast.js).
export default function OnlineStoreSettings(props) {
  return <ToastProvider><OnlineStoreSettingsInner {...props} /></ToastProvider>;
}

function OnlineStoreSettingsInner({ token, darkMode = false, T: theme, onLeadsChanged }) {
  const T = theme || { bg2: '#fff', bg3: '#f8faff', border: '#e8edf5', border2: '#e2e8f0', text: '#0f1f45', text2: '#4a5568', text3: '#94a3b8', cardBorder: '#f1f5f9', inputBg: '#fff', inputBorder: '#e2e8f0' };
  const [tab, setTab] = useState('orders');
  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${BASE}/api/store${path}`, {
      ...opts,
      headers: { Authorization: `Bearer ${token}`, ...(opts.body ? { 'Content-Type': 'application/json' } : {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      // No JSON message usually means the server doesn't have this route yet (backend not deployed).
      throw new Error(data.message || (res.status === 404
        ? 'The server does not support this yet. Please deploy the latest backend.'
        : `Request failed (${res.status}). Please try again.`));
    }
    return data;
  }, [token]);

  const ui = {
    T, darkMode, api, onLeadsChanged,
    card: { background: T.bg2, border: `1px solid ${T.cardBorder}`, borderRadius: 16, padding: 22, boxShadow: darkMode ? '0 4px 24px rgba(0,0,0,0.3)' : '0 2px 12px rgba(15,31,69,0.06)' },
    input: { width: '100%', border: `1px solid ${T.inputBorder}`, borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none', background: T.inputBg, color: T.text, boxSizing: 'border-box', fontFamily: 'inherit' },
    label: { display: 'block', fontSize: 11, fontWeight: 600, color: T.text3, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' },
    primary: { padding: '9px 20px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#0f1f45,#1a3a6b)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    ghost: { padding: '7px 14px', borderRadius: 9, border: `1px solid ${T.border}`, background: T.bg2, color: T.text, fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: T.text, margin: 0 }}>Online Store</h3>
        <p style={{ fontSize: 12, color: T.text3, marginTop: 4 }}>
          Orders and quote requests from your client app. Each one is also in the lead list (source "Mobile App").
        </p>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: `1px solid ${T.border}` }}>
        {[['orders', 'Orders'], ['users', 'App users'], ['services', 'Services in store'], ['banners', 'Banners & offers'], ['settings', 'Store settings']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '9px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            color: tab === k ? T.text : T.text3, borderBottom: `2px solid ${tab === k ? '#f97316' : 'transparent'}`, marginBottom: -1,
          }}>{l}</button>
        ))}
      </div>
      {tab === 'orders' && <Orders ui={ui} />}
      {tab === 'users' && <AppUsers ui={ui} />}
      {tab === 'services' && <Services ui={ui} />}
      {tab === 'banners' && <Banners ui={ui} />}
      {tab === 'settings' && <StoreSettings ui={ui} />}
    </div>
  );
}

function Note({ ui, tone = 'info', children }) {
  const c = tone === 'warn' ? ['#fffbeb', '#fde68a', '#92400e'] : ['#eff6ff', '#bfdbfe', '#1e40af'];
  return (
    <div style={{ background: ui.darkMode ? 'rgba(59,130,246,0.08)' : c[0], border: `1px solid ${ui.darkMode ? 'rgba(59,130,246,0.2)' : c[1]}`, color: ui.darkMode ? '#93c5fd' : c[2], borderRadius: 10, padding: '10px 14px', fontSize: 12, lineHeight: 1.5, marginBottom: 14 }}>
      {children}
    </div>
  );
}

// ── Store settings ─────────────────────────────────────────────────────────
function StoreSettings({ ui }) {
  const { T, api } = ui;
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { api('/admin/settings').then(setForm).catch(e => setMsg(e.message)); }, [api]);
  if (!form) return <div style={{ color: T.text3, fontSize: 13 }}>{msg || 'Loading…'}</div>;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setSaving(true); setMsg('');
    try { const d = await api('/admin/settings', { method: 'PUT', body: JSON.stringify(form) }); setForm(f => ({ ...f, ...d })); setMsg('Saved'); }
    catch (e) { setMsg(e.message); }
    setSaving(false);
  };
  const pay = form.payment || {};
  const field = (k, label, ph) => (
    <div><label style={ui.label}>{label}</label><input style={ui.input} value={form[k] || ''} onChange={set(k)} placeholder={ph} /></div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!form.portal_slug && <Note ui={ui} tone="warn">Set a portal slug first (Settings → Client Portal). The app finds your store by it.</Note>}
      <div style={ui.card}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600, color: T.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.enabled} onChange={set('enabled')} style={{ width: 18, height: 18 }} />
          Show the store in the client app
        </label>
        <p style={{ fontSize: 12, color: T.text3, margin: '6px 0 16px 28px' }}>Only services you switch on under "Services in store" are listed.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
          {field('tagline', 'Tagline (home screen)', 'e.g. Company registration, GST and compliance')}
          {field('phone', 'Call number', '+91 …')}
          {field('whatsapp', 'WhatsApp number', '+91 …')}
          {field('email', 'Contact email', 'info@yourfirm.com')}
          {field('notify_email', 'Send new-order alerts to', 'you@yourfirm.com (comma-separated)')}
        </div>
      </div>

      <div style={ui.card}>
        <h4 style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 10px' }}>How customers pay</h4>
        <p style={{ fontSize: 12, color: T.text3, margin: '0 0 12px' }}>
          From Billing Settings. Customers pay by UPI (QR) or bank transfer and enter the UTR; you confirm the payment under Orders.
        </p>
        <div style={{ fontSize: 13, color: T.text2, lineHeight: 1.8 }}>
          <div>UPI: <b>{pay.upi_id || 'not set'}</b></div>
          <div>Bank: <b>{[pay.bank_name, pay.bank_account, pay.bank_ifsc].filter(Boolean).join(' · ') || 'not set'}</b></div>
        </div>
        {!pay.upi_id && !pay.bank_account && <div style={{ marginTop: 12 }}><Note ui={ui} tone="warn">Add a UPI ID or bank account in Billing Settings, or customers can't pay in the app.</Note></div>}
        <label style={{ ...ui.label, marginTop: 12 }}>Note shown on the payment screen (optional)</label>
        <input style={ui.input} value={form.payment_note || ''} onChange={set('payment_note')} placeholder="e.g. Please mention your order number in the payment remark" />
      </div>

      <div style={ui.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: 0 }}>Refund & cancellation policy</h4>
          {!form.refund_policy && <button style={ui.ghost} onClick={() => setForm(f => ({ ...f, refund_policy: REFUND_TEMPLATE }))}>Use a starting draft</button>}
        </div>
        <p style={{ fontSize: 12, color: T.text3, margin: '0 0 10px' }}>Shown in the app before checkout. Review the draft with your own terms before switching the store on.</p>
        <textarea style={{ ...ui.input, minHeight: 200, resize: 'vertical', lineHeight: 1.5 }} value={form.refund_policy || ''} onChange={set('refund_policy')} />
      </div>

      <ConsultSettings ui={ui} form={form} setForm={setForm} />

      <NotificationSettings ui={ui} form={form} setForm={setForm} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button style={{ ...ui.primary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        {msg && <span style={{ fontSize: 13, color: msg === 'Saved' ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{msg}</span>}
      </div>
    </div>
  );
}

// Paid consultation: customers book a 15 / 30 minute video or phone call in a
// free 30-minute slot and pay like a store order. Staff confirm with a meeting link.
const WEEKDAYS = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun']];
function ConsultSettings({ ui, form, setForm }) {
  const { T } = ui;
  const days = String(form.consult_days ?? '1,2,3,4,5,6').split(',').filter(Boolean);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleDay = (d) => set('consult_days', (days.includes(d) ? days.filter(x => x !== d) : [...days, d]).join(','));
  return (
    <div style={ui.card}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 700, color: T.text, cursor: 'pointer' }}>
        <input type="checkbox" checked={!!form.consult_enabled} onChange={e => set('consult_enabled', e.target.checked)} style={{ width: 18, height: 18 }} />
        Paid consultation ("Talk to an expert")
      </label>
      <p style={{ fontSize: 12, color: T.text3, margin: '6px 0 14px 28px' }}>
        Customers pick 15 or 30 minutes, video or phone, and a free time slot, then pay like a store order.
        Confirm it under Orders and add the meeting link (Google Meet / Zoom) for video calls.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
        <div><label style={ui.label}>15 minutes (₹, incl. GST)</label>
          <input type="number" min="1" style={ui.input} value={form.consult_price_15 ?? 500} onChange={e => set('consult_price_15', e.target.value)} /></div>
        <div><label style={ui.label}>30 minutes (₹, incl. GST)</label>
          <input type="number" min="1" style={ui.input} value={form.consult_price_30 ?? 900} onChange={e => set('consult_price_30', e.target.value)} /></div>
        <div><label style={ui.label}>From</label>
          <input type="time" style={ui.input} value={form.consult_start || '10:00'} onChange={e => set('consult_start', e.target.value)} /></div>
        <div><label style={ui.label}>To</label>
          <input type="time" style={ui.input} value={form.consult_end || '18:00'} onChange={e => set('consult_end', e.target.value)} /></div>
      </div>
      <label style={{ ...ui.label, marginTop: 14 }}>Days</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {WEEKDAYS.map(([d, l]) => (
          <button key={d} type="button" onClick={() => toggleDay(d)} style={{
            ...ui.ghost, borderRadius: 18, minWidth: 52,
            background: days.includes(d) ? T.text : T.bg2, color: days.includes(d) ? T.bg2 : T.text,
          }}>{l}</button>
        ))}
      </div>
      <p style={{ fontSize: 12, color: T.text3, margin: '10px 0 0' }}>Slots are every 30 minutes, one booking each, bookable up to 10 days ahead and at least 2 hours in advance.</p>
    </div>
  );
}

// Who hears about each order event, on which channel. The event list and the
// defaults come from the backend (EVENTS in storeRoutes.js).
const CHANNEL_COLS = [
  { key: 'app',        group: 'Customer', label: 'App bell' },
  { key: 'email',      group: 'Customer', label: 'Email' },
  { key: 'team_bell',  group: 'Your team', label: 'Bell (admins)' },
  { key: 'team_email', group: 'Your team', label: 'Alert email' },
];

function NotificationSettings({ ui, form, setForm }) {
  const { T } = ui;
  const events = form.events || [];
  const cur = form.notify_events || {};
  const toggle = (ev, ch) => setForm(f => ({
    ...f, notify_events: { ...f.notify_events, [ev]: { ...f.notify_events?.[ev], [ch]: !f.notify_events?.[ev]?.[ch] } },
  }));
  if (!events.length) return null;
  const cell = { padding: '10px 8px', textAlign: 'center', borderBottom: `1px solid ${T.cardBorder}` };

  return (
    <div style={ui.card}>
      <h4 style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Notifications</h4>
      <p style={{ fontSize: 12, color: T.text3, margin: '0 0 14px' }}>
        Choose who is told about each step of an order. The customer sees app notifications under the bell in the app
        (it checks every 30 seconds while open). Customer emails need their email on the order and your email set up in
        Settings → Notifications → Email. "Alert email" goes to the address above.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: 'left' }} />
              <th colSpan={2} style={{ ...cell, fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</th>
              <th colSpan={2} style={{ ...cell, fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your team</th>
            </tr>
            <tr>
              <th style={{ ...cell, textAlign: 'left', fontSize: 12, color: T.text3, fontWeight: 600 }}>When</th>
              {CHANNEL_COLS.map(c => <th key={c.key} style={{ ...cell, fontSize: 12, color: T.text2, fontWeight: 600 }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {events.map(ev => (
              <tr key={ev.key}>
                <td style={{ ...cell, textAlign: 'left' }}>
                  <div style={{ fontWeight: 600, color: T.text }}>{ev.label}</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>{ev.hint}</div>
                </td>
                {CHANNEL_COLS.map(c => (
                  <td key={c.key} style={cell}>
                    <input type="checkbox" checked={!!cur[ev.key]?.[c.key]} onChange={() => toggle(ev.key, c.key)}
                      style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#0f1f45' }} aria-label={`${ev.label}: ${c.group} ${c.label}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!form.notify_email && <p style={{ fontSize: 12, color: T.text3, margin: '10px 0 0' }}>Set "Send new-order alerts to" above for the alert emails.</p>}
    </div>
  );
}

// ── Services in store ─────────────────────────────────────────────────────
function Services({ ui }) {
  const { T, api } = ui;
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState('');
  const load = useCallback(() => api('/admin/services').then(setList).catch(() => setList([])), [api]);
  useEffect(() => { load(); }, [load]);
  if (!list) return <div style={{ color: T.text3, fontSize: 13 }}>Loading…</div>;
  const shown = list.filter(s => !q || `${s.name} ${s.category || ''}`.toLowerCase().includes(q.toLowerCase()));
  const count = list.filter(s => s.store_visible).length;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...ui.input, maxWidth: 280 }} placeholder="Search services" value={q} onChange={e => setQ(e.target.value)} />
        <span style={{ fontSize: 12, color: T.text3 }}>{count} in store · keep it to your popular services</span>
      </div>
      <div style={{ ...ui.card, padding: 0, overflow: 'hidden' }}>
        {shown.map(s => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: `1px solid ${T.cardBorder}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{s.name}</div>
              <div style={{ fontSize: 11, color: T.text3 }}>{[s.category, s.status !== 'active' ? 'inactive' : null].filter(Boolean).join(' · ')}</div>
            </div>
            {s.store_visible ? (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#16a34a' }}>
                In store · {s.store_price_type === 'fixed' ? inr(s.store_price) : 'Quote'}
              </span>
            ) : <span style={{ fontSize: 12, color: T.text3 }}>Not listed</span>}
            <button style={ui.ghost} onClick={() => setEdit(s)}>{s.store_visible ? 'Edit' : 'Add to store'}</button>
          </div>
        ))}
        {!shown.length && <div style={{ padding: 24, textAlign: 'center', color: T.text3, fontSize: 13 }}>No services found. Add services in Services first.</div>}
      </div>
      {edit && <ServiceEditor ui={ui} s={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function Modal({ ui, title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: ui.T.bg2, borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: ui.T.text }}>{title}</h4>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: ui.T.text3 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ServiceEditor({ ui, s, onClose, onSaved }) {
  const { T, api } = ui;
  const [f, setF] = useState({
    store_price_type: s.store_price_type || 'quote',
    store_price: s.store_price ?? s.default_amount ?? '', store_summary: s.store_summary || s.description || '',
    store_includes: s.store_includes || '', store_documents: s.store_documents || '', store_turnaround: s.store_turnaround || '',
    store_sort: s.store_sort || 0, store_video_url: s.store_video_url || '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async (visible = f.store_visible) => {
    setSaving(true); setErr('');
    try { await api(`/admin/services/${s.id}`, { method: 'PUT', body: JSON.stringify({ ...f, store_visible: visible }) }); onSaved(); }
    catch (e) { setErr(e.message); setSaving(false); }
  };
  const gst = Number(s.gst_percent || 0);
  const price = Number(f.store_price) || 0;

  return (
    <Modal ui={ui} title={s.name} onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['fixed', 'Fixed price'], ['quote', 'Request a quote']].map(([k, l]) => (
          <button key={k} onClick={() => setF(x => ({ ...x, store_price_type: k }))} style={{
            ...ui.ghost, flex: 1, padding: '10px', borderColor: f.store_price_type === k ? '#f97316' : T.border,
            color: f.store_price_type === k ? '#c2410c' : T.text,
          }}>{l}</button>
        ))}
      </div>
      {f.store_price_type === 'fixed' && (
        <div style={{ marginBottom: 14 }}>
          <label style={ui.label}>Price before GST (₹)</label>
          <input type="number" min="0" style={ui.input} value={f.store_price} onChange={set('store_price')} />
          <div style={{ fontSize: 12, color: T.text3, marginTop: 5 }}>
            GST {gst}% (from the service) → customer pays <b style={{ color: T.text }}>{inr(Math.round(price * (1 + gst / 100) * 100) / 100)}</b>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div><label style={ui.label}>YouTube video (optional)</label>
          <input style={ui.input} value={f.store_video_url} onChange={set('store_video_url')} placeholder="https://youtu.be/… or youtube.com/watch?v=…" />
          <div style={{ fontSize: 12, color: T.text3, marginTop: 5 }}>Plays at the top of this service in the app and under "Watch &amp; learn" on Home.</div></div>
        <div><label style={ui.label}>Short description</label>
          <input style={ui.input} value={f.store_summary} onChange={set('store_summary')} placeholder="One line, shown on the service card" /></div>
        <div><label style={ui.label}>What's included (one per line)</label>
          <textarea style={{ ...ui.input, minHeight: 80 }} value={f.store_includes} onChange={set('store_includes')} /></div>
        <div><label style={ui.label}>Documents needed (one per line)</label>
          <textarea style={{ ...ui.input, minHeight: 80 }} value={f.store_documents} onChange={set('store_documents')} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div><label style={ui.label}>Time taken</label>
            <input style={ui.input} value={f.store_turnaround} onChange={set('store_turnaround')} placeholder="e.g. 7–10 working days" /></div>
          <div><label style={ui.label}>Order (lower first)</label>
            <input type="number" style={ui.input} value={f.store_sort} onChange={set('store_sort')} /></div>
        </div>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
        {s.store_visible ? <button style={{ ...ui.ghost, color: '#dc2626' }} disabled={saving} onClick={() => save(false)}>Remove from store</button> : <span />}
        <button style={{ ...ui.primary, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={() => save(true)}>{saving ? 'Saving…' : s.store_visible ? 'Save' : 'Add to store'}</button>
      </div>
    </Modal>
  );
}

// ── Banners & offers ──────────────────────────────────────────────────────
// Home slider in the app. A banner can open a store service and give it an
// offer price between its dates (fixed-price services only); after the end
// date the banner and its offer go away by themselves.
const THEMES = [
  ['festive', 'Festive (saffron)', '#FFF7EF', '#C2410C'],
  ['blue', 'Calm blue', '#EEF3FF', '#2F55D4'],
  ['green', 'Fresh green', '#EDF8F1', '#1E8A4C'],
];

function Banners({ ui }) {
  const { T, api } = ui;
  const [list, setList] = useState(null);
  const [services, setServices] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api('/admin/banners').then(setList).catch(() => setList([])), [api]);
  useEffect(() => {
    load();
    api('/admin/services').then(r => setServices(r.filter(x => x.store_visible))).catch(() => {});
  }, [load, api]);
  if (!list) return <div style={{ color: T.text3, fontSize: 13 }}>Loading…</div>;
  const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: T.text3 }}>Shown on the app Home between their dates. 2–4 at a time works best.</span>
        <button style={ui.primary} onClick={() => setEdit({})}>+ New banner</button>
      </div>
      <div style={{ ...ui.card, padding: 0, overflow: 'hidden' }}>
        {list.map(b => {
          const th = THEMES.find(t => t[0] === b.theme) || THEMES[0];
          const when = b.starts_on || b.ends_on ? `${fmt(b.starts_on) || 'now'} – ${fmt(b.ends_on) || 'no end'}` : 'always';
          return (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderBottom: `1px solid ${T.cardBorder}` }}>
              <span style={{ width: 8, height: 40, borderRadius: 4, background: th[3], flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{b.tag ? `${b.tag} · ` : ''}{b.title}</div>
                <div style={{ fontSize: 12, color: T.text3 }}>
                  {[b.service_name ? `Opens ${b.service_name}` : 'No service',
                    b.offer_price ? `offer ${inr(b.offer_price)} (usual ${inr(b.store_price)})` : null, when].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20, color: b.live ? '#16a34a' : '#6b7280', background: b.live ? '#f0fdf4' : '#f3f4f6' }}>
                {b.live ? 'Live' : b.active ? 'Not in dates' : 'Off'}
              </span>
              <button style={ui.ghost} onClick={() => setEdit(b)}>Edit</button>
            </div>
          );
        })}
        {!list.length && <div style={{ padding: 28, textAlign: 'center', color: T.text3, fontSize: 13 }}>No banners yet. Create one for your festive offer.</div>}
      </div>
      {edit && <BannerEditor ui={ui} b={edit} services={services} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function BannerEditor({ ui, b, services, onClose, onSaved }) {
  const { T, api } = ui;
  const day = (d) => (d ? String(d).slice(0, 10) : '');
  const [f, setF] = useState({
    tag: b.tag ?? 'NAVRATRI OFFER', title: b.title || '', subtitle: b.subtitle || '', cta: b.cta || 'Book now',
    theme: b.theme || 'festive', service_id: b.service_id || '', offer_price: b.offer_price ?? '',
    starts_on: day(b.starts_on), ends_on: day(b.ends_on), active: b.active !== false, sort: b.sort || 0,
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const svc = services.find(x => String(x.id) === String(f.service_id));
  const fixed = !!svc && svc.store_price_type === 'fixed';
  const offer = fixed && Number(f.offer_price) > 0 ? Number(f.offer_price) : null;
  const th = THEMES.find(t => t[0] === f.theme) || THEMES[0];

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await api(b.id ? `/admin/banners/${b.id}` : '/admin/banners', {
        method: b.id ? 'PUT' : 'POST', body: JSON.stringify({ ...f, offer_price: offer || '' }),
      });
      onSaved();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  const { showToast, showConfirm } = useToast();
  const remove = async () => {
    if (!(await showConfirm('This banner will be removed from the app.', { title: 'Delete banner?', confirmLabel: 'Delete' }))) return;
    try { await api(`/admin/banners/${b.id}`, { method: 'DELETE' }); showToast('Banner deleted.', 'success'); onSaved(); } catch (e) { setErr(e.message); }
  };

  return (
    <Modal ui={ui} title={b.id ? 'Edit banner' : 'New banner'} onClose={onClose}>
      <div style={{ borderRadius: 14, padding: 14, background: th[2], border: '1px solid rgba(0,0,0,0.06)', marginBottom: 16 }}>
        {f.tag && <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: th[3] }}>{f.tag}</div>}
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{f.title || 'Your heading'}</div>
        {(f.subtitle || offer) && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
            {f.subtitle}{offer ? ` · ${inr(offer)} + GST (usual ${inr(svc.store_price)})` : ''}
          </div>
        )}
        <div style={{ fontSize: 12, fontWeight: 700, color: th[3], marginTop: 8 }}>{f.cta || 'Book now'} →</div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <div><label style={ui.label}>Tag</label><input style={ui.input} value={f.tag} onChange={set('tag')} placeholder="NAVRATRI OFFER" /></div>
          <div><label style={ui.label}>Heading</label><input style={ui.input} value={f.title} onChange={set('title')} placeholder="Start your business this festive season" /></div>
        </div>
        <div><label style={ui.label}>Line under it</label>
          <input style={ui.input} value={f.subtitle} onChange={set('subtitle')} placeholder="Private Limited Company at a festive price" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <div><label style={ui.label}>Opens service</label>
            <select style={ui.input} value={f.service_id} onChange={set('service_id')}>
              <option value="">— none —</option>
              {services.map(x => (
                <option key={x.id} value={x.id}>{x.name}{x.store_price_type === 'fixed' ? ` (${inr(x.store_price)})` : ' (quote)'}</option>
              ))}
            </select></div>
          <div><label style={ui.label}>Button text</label><input style={ui.input} value={f.cta} onChange={set('cta')} /></div>
        </div>
        {fixed && (
          <div><label style={ui.label}>Offer price before GST (₹, optional)</label>
            <input type="number" min="1" style={ui.input} value={f.offer_price} onChange={set('offer_price')} placeholder={`Usual price ${svc.store_price}`} />
            <div style={{ fontSize: 12, color: T.text3, marginTop: 5 }}>
              Between the dates the app shows this price with the usual one struck through, and orders are placed at it.
            </div></div>
        )}
        {svc && !fixed && <div style={{ fontSize: 12, color: T.text3 }}>This service is "Request a quote", so there is no offer price; the banner just opens it.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <div><label style={ui.label}>From</label><input type="date" style={ui.input} value={f.starts_on} onChange={set('starts_on')} /></div>
          <div><label style={ui.label}>Till (incl.)</label><input type="date" style={ui.input} value={f.ends_on} onChange={set('ends_on')} /></div>
          <div><label style={ui.label}>Order</label><input type="number" style={ui.input} value={f.sort} onChange={set('sort')} /></div>
        </div>
        <div><label style={ui.label}>Look</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {THEMES.map(t => (
              <button key={t[0]} type="button" onClick={() => setF(x => ({ ...x, theme: t[0] }))}
                style={{ ...ui.ghost, background: t[2], color: t[3], borderColor: f.theme === t[0] ? t[3] : T.border }}>{t[1]}</button>
            ))}
          </div></div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={f.active} onChange={set('active')} /> Show this banner
        </label>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18 }}>
        {b.id ? <button style={{ ...ui.ghost, color: '#dc2626' }} onClick={remove}>Delete</button> : <span />}
        <button style={{ ...ui.primary, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save banner'}</button>
      </div>
    </Modal>
  );
}

// ── App users ─────────────────────────────────────────────────────────────
// Everyone who created an account in the white-label app. New people are CRM
// leads; existing clients (same email / mobile) are marked as such. To give an
// existing client their Work / Documents / Accounts in the app, put this email
// on the client and turn on "Client login" in Clients.
function AppUsers({ ui }) {
  const { T, api } = ui;
  const [list, setList] = useState(null);
  const [q, setQ] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => api('/admin/app-users').then(setList).catch(e => { setErr(e.message); setList([]); }), [api]);
  useEffect(() => { load(); }, [load]);
  const { isAdmin } = usePermissions();
  // Admin only (the backend checks too). Orders stay; the app signs this person out on its next start.
  const { showToast, showConfirm } = useToast();
  const remove = async (u) => {
    if (!(await showConfirm('Orders stay. They are signed out and can register again.', { title: `Delete ${u.name || u.email}?`, confirmLabel: 'Delete account' }))) return;
    const withLead = !!u.lead_code && await showConfirm('Kept if it has orders or is Won / Lost.', { title: `Also delete CRM lead ${u.lead_code}?`, confirmLabel: 'Delete lead too', confirmColor: '#dc2626' });
    try {
      await api(`/admin/app-users/${u.id}${withLead ? '?with_lead=1' : ''}`, { method: 'DELETE' });
      showToast('App account deleted.', 'success');
      ui.onLeadsChanged?.();
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };
  if (!list) return <div style={{ color: T.text3, fontSize: 13 }}>Loading…</div>;
  if (err) return <Note ui={ui} tone="warn">Could not load app users: {err}</Note>;
  const shown = list.filter(u => !q || `${u.name} ${u.email} ${u.mobile}`.toLowerCase().includes(q.toLowerCase()));
  const clients = list.filter(u => u.matched_client).length;
  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${T.border}` };
  const td = { padding: '11px 14px', fontSize: 13, color: T.text, borderBottom: `1px solid ${T.cardBorder}`, verticalAlign: 'top' };
  const pill = (text, color, bg) => <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>{text}</span>;

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input style={{ ...ui.input, maxWidth: 280 }} placeholder="Search name, email, mobile" value={q} onChange={e => setQ(e.target.value)} />
        <span style={{ fontSize: 12, color: T.text3 }}>{list.length} registered · {clients} existing client{clients === 1 ? '' : 's'} · {list.length - clients} new</span>
      </div>
      <div style={{ ...ui.card, padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr><th style={th}>Name</th><th style={th}>Email · Mobile</th><th style={th}>Who</th><th style={th}>App access</th><th style={th}>Orders</th><th style={th}>Registered</th>{isAdmin && <th style={th} />}</tr>
          </thead>
          <tbody>
            {shown.map(u => (
              <tr key={u.id}>
                <td style={{ ...td, fontWeight: 600 }}>{u.name || '—'}
                  {u.partner_requested_at && <div style={{ marginTop: 4 }}>{pill(`Wants to be a partner${u.partner_profession ? ` · ${u.partner_profession}` : ''}`, '#7c3aed', '#f5f3ff')}</div>}
                </td>
                <td style={td}>{u.email}<div style={{ fontSize: 12, color: T.text3 }}>{u.mobile}</div></td>
                <td style={td}>
                  {u.matched_client
                    ? pill(`Client: ${u.matched_client}`, '#16a34a', '#f0fdf4')
                    : u.lead_code ? pill(`Lead ${u.lead_code}${u.lead_stage ? ` · ${u.lead_stage}` : ''}`, '#2563eb', '#eff6ff') : '—'}
                </td>
                <td style={td}>
                  {u.portal_access
                    ? pill('Work, documents & invoices', '#16a34a', '#f0fdf4')
                    : <span style={{ fontSize: 12, color: T.text3 }} title='Put this email on the client and turn on "Client login" in Clients'>Store only</span>}
                </td>
                <td style={td}>{u.orders || 0}</td>
                <td style={{ ...td, fontSize: 12, color: T.text2 }}>{when(u.created_at)}</td>
                {isAdmin && (
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button onClick={() => remove(u)} title="Delete app account"
                      style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <div style={{ padding: 28, textAlign: 'center', color: T.text3, fontSize: 13 }}>No one has registered on the app yet.</div>}
      </div>
      <p style={{ fontSize: 12, color: T.text3, marginTop: 10, lineHeight: 1.5 }}>
        To show an existing client their work, documents and invoices in the app: open the client in Clients, set this email and turn on "Client login".
        In the app they then tap their profile → "Existing client? See your work" (or sign in again).
        Partner requests: add the person as a client with Client login and mark them a referral partner (Referral Partners); the Refer tab then appears in their app.
      </p>
    </div>
  );
}

// ── Orders ─────────────────────────────────────────────────────────────────
function Orders({ ui }) {
  const { T, api } = ui;
  const [filter, setFilter] = useState('');
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api(`/admin/orders${filter ? `?status=${filter}` : ''}`).then(setList).catch(() => setList([])), [api, filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {[['', 'All'], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ ...ui.ghost, borderRadius: 20, background: filter === k ? T.text : T.bg2, color: filter === k ? T.bg2 : T.text }}>{l}</button>
        ))}
      </div>
      {!list ? <div style={{ color: T.text3, fontSize: 13 }}>Loading…</div> : (
        <div style={{ ...ui.card, padding: 0, overflow: 'hidden' }}>
          {list.map(o => {
            const st = STATUS[o.status] || STATUS.enquiry;
            return (
              <button key={o.id} onClick={() => setOpen(o)} style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 12, padding: '12px 16px', border: 'none', borderBottom: `1px solid ${T.cardBorder}`, background: 'none', cursor: 'pointer' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{o.service_name}</div>
                  <div style={{ fontSize: 11, color: T.text3 }}>{o.order_no} · {o.customer_name} · {o.customer_mobile} · {o.kind === 'consultation' ? `call ${when(o.slot_at)}` : when(o.created_at)}</div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{o.total != null ? inr(o.total) : 'Quote'}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: st.color, background: st.bg, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>{st.label}</span>
              </button>
            );
          })}
          {!list.length && <div style={{ padding: 28, textAlign: 'center', color: T.text3, fontSize: 13 }}>No orders yet.</div>}
        </div>
      )}
      {open && <OrderSheet ui={ui} o={open} onClose={() => setOpen(null)} onChanged={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function OrderSheet({ ui, o, onClose, onChanged }) {
  const { T, api } = ui;
  const [amount, setAmount] = useState(o.amount ?? '');
  const [gst, setGst] = useState(o.gst_percent ?? 18);
  const [note, setNote] = useState('');
  const [link, setLink] = useState(o.meet_link || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const { showToast, showConfirm } = useToast();
  const act = async (path, body, confirmText) => {
    if (confirmText && !(await showConfirm(confirmText, { title: 'Please confirm', confirmLabel: 'Yes, continue', confirmColor: '#0f1f45' }))) return;
    setBusy(true); setMsg('');
    try {
      const d = await api(`/admin/orders/${o.id}/${path}`, { method: 'PUT', body: JSON.stringify(body) });
      if (d.email && !d.email.success && o.customer_email) showToast(`Saved, but the customer email wasn't sent: ${d.email.reason}`, 'error');
      else showToast('Order updated.', 'success');
      ui.onLeadsChanged?.();
      onChanged();
    } catch (e) { setMsg(e.message); setBusy(false); }
  };
  // Deleting (e.g. test orders) is admin-only; the backend checks this too.
  const { isAdmin } = usePermissions();
  const [withLead, setWithLead] = useState(true);
  const remove = async () => {
    if (!(await showConfirm(withLead && o.lead_id ? "Its CRM lead is deleted too. This can't be undone." : "This can't be undone.", { title: `Delete ${o.order_no}?`, confirmLabel: 'Delete order' }))) return;
    setBusy(true); setMsg('');
    try {
      const d = await api(`/admin/orders/${o.id}${withLead ? '?with_lead=1' : ''}`, { method: 'DELETE' });
      if (withLead && o.lead_id && !d.lead_deleted) showToast('Order deleted. Lead kept — it is already Won / Lost.', 'info');
      else showToast('Order deleted.', 'success');
      ui.onLeadsChanged?.();
      onChanged();
    } catch (e) { setMsg(e.message); setBusy(false); }
  };
  const st = STATUS[o.status] || STATUS.enquiry;
  const row = (k, v) => v ? <div style={{ display: 'flex', gap: 12, fontSize: 13, padding: '5px 0' }}><span style={{ width: 110, color: T.text3 }}>{k}</span><span style={{ color: T.text, flex: 1, whiteSpace: 'pre-wrap' }}>{v}</span></div> : null;
  const open = o.status !== 'confirmed' && o.status !== 'cancelled';
  // Paid consultation: a booked time slot, video or phone; video calls need a meeting link.
  const consult = o.kind === 'consultation';
  const video = consult && o.consult_mode !== 'phone';
  const waMsg = encodeURIComponent(`Hello ${o.customer_name}, regarding your order ${o.order_no} (${o.service_name}).`);
  const waNo = String(o.customer_mobile || '').replace(/\D/g, '').replace(/^(\d{10})$/, '91$1');

  return (
    <Modal ui={ui} title={`${o.order_no} · ${o.service_name}`} onClose={onClose}>
      <span style={{ fontSize: 11, fontWeight: 700, color: st.color, background: st.bg, padding: '4px 10px', borderRadius: 20 }}>{st.label}</span>
      <div style={{ marginTop: 12 }}>
        {row('Customer', o.customer_name)}
        {row('Mobile', o.customer_mobile)}
        {row('Email', o.customer_email || '—')}
        {row('Client', o.client_name)}
        {row('CRM lead', o.lead_code)}
        {consult && row('Booked for', new Date(o.slot_at).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }))}
        {consult && row('Call', `${o.duration_min} min · ${video ? 'Video call' : 'Phone call (you call the customer)'}`)}
        {consult && row('Meeting link', o.meet_link)}
        {row(consult ? 'Topic' : 'Message', o.message)}
        {row('Amount', o.total != null ? `${inr(o.total)} (${inr(o.amount)} + GST ${Number(o.gst_percent)}%)` : 'Quote requested')}
        {row('UTR / ref', o.payment_ref)}
        {row('Paid on', when(o.payment_submitted_at))}
        {row('Staff note', o.staff_note)}
        {row('Placed', when(o.created_at))}
      </div>
      {waNo && <a href={`https://wa.me/${waNo}?text=${waMsg}`} target="_blank" rel="noreferrer" style={{ ...ui.ghost, display: 'inline-block', marginTop: 10, textDecoration: 'none', color: '#16a34a' }}>WhatsApp customer ↗</a>}

      {!consult && (o.status === 'enquiry' || o.status === 'awaiting_payment') && (
        <div style={{ marginTop: 18, padding: 14, borderRadius: 12, background: T.bg3 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>{o.status === 'enquiry' ? 'Send a price' : 'Change the price'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <div><label style={ui.label}>Price before GST (₹)</label><input type="number" style={ui.input} value={amount} onChange={e => setAmount(e.target.value)} /></div>
            <div><label style={ui.label}>GST %</label><input type="number" style={ui.input} value={gst} onChange={e => setGst(e.target.value)} /></div>
          </div>
          <div style={{ fontSize: 12, color: T.text3, margin: '8px 0' }}>The customer sees "Pay {inr(Math.round((Number(amount) || 0) * (1 + (Number(gst) || 0) / 100) * 100) / 100)}" in the app{o.customer_email ? ' and gets an email' : ''}.</div>
          <button style={ui.primary} disabled={busy} onClick={() => act('price', { amount, gst_percent: gst, note })}>Send price</button>
        </div>
      )}

      {open && (
        <div style={{ marginTop: 16 }}>
          {video && (
            <div style={{ marginBottom: 12 }}>
              <label style={ui.label}>Meeting link (Google Meet / Zoom)</label>
              <input style={ui.input} value={link} onChange={e => setLink(e.target.value)} placeholder="https://meet.google.com/…" />
              <div style={{ fontSize: 12, color: T.text3, marginTop: 5 }}>Sent to the customer with the confirmation; they tap Join in the app. You can add it later too.</div>
            </div>
          )}
          <label style={ui.label}>Note (optional, saved on the order)</label>
          <input style={ui.input} value={note} onChange={e => setNote(e.target.value)} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
            <button style={{ ...ui.ghost, color: '#dc2626' }} disabled={busy} onClick={() => act('cancel', { note }, 'The customer is notified of the cancellation.')}>Cancel order</button>
            <button style={{ ...ui.primary, background: '#16a34a' }} disabled={busy}
              onClick={() => act('confirm', { note, meet_link: video ? link.trim() : undefined }, o.status === 'payment_submitted'
                ? `Check ${inr(o.total)} (ref ${o.payment_ref}) is in your bank first.`
                : 'No payment is recorded. Confirm anyway (e.g. paid at the office)?')}>
              {o.status === 'payment_submitted' ? 'Payment received, confirm' : 'Confirm order'}
            </button>
          </div>
        </div>
      )}
      {video && o.status === 'confirmed' && (
        <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: T.bg3 }}>
          <label style={ui.label}>Meeting link (Google Meet / Zoom)</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={ui.input} value={link} onChange={e => setLink(e.target.value)} placeholder="https://meet.google.com/…" />
            <button style={{ ...ui.primary, whiteSpace: 'nowrap' }} disabled={busy || !link.trim()} onClick={() => act('meeting', { meet_link: link.trim() })}>Save &amp; send</button>
          </div>
        </div>
      )}
      {msg && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{msg}</div>}

      {isAdmin && (
        <div style={{ marginTop: 20, paddingTop: 14, borderTop: `1px solid ${T.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          {o.lead_id ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.text2, cursor: 'pointer' }}>
              <input type="checkbox" checked={withLead} onChange={e => setWithLead(e.target.checked)} />
              Also delete its CRM lead{o.lead_code ? ` (${o.lead_code})` : ''}
            </label>
          ) : <span />}
          <button style={{ border: 'none', background: 'none', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }} disabled={busy} onClick={remove}>
            Delete order permanently
          </button>
        </div>
      )}
    </Modal>
  );
}
